import * as React from "react";
import { Users, DollarSign, TrendingUp, TrendingDown, Bell, X, ChevronLeft } from "lucide-react";
import AttendanceChart from "../components/dashboard/attendance-chart";
import PerformanceChart from "../components/dashboard/performance-chart";
import NotificationsAndEvents from "../components/dashboard/notifications-and-events";
import { ResizablePanel, ResizableHandle, ResizablePanelGroup } from "../components/ui/resizable";
import FailingStudents from "../components/dashboard/failing-students";
import RecentPayments from "../components/dashboard/recent-payments";
import { useGetChildren } from '../api/student/studentQueries';
import { useGetAllStaff } from '../api/staff/staffQueries';

export default function DashboardContent() {
  const { data: studentsData, isLoading: isLoadingStudents } = useGetChildren();
  const { data: staffData, isLoading: isLoadingStaff } = useGetAllStaff();

  const studentCount = studentsData?.length || 0;
  const staffCount = staffData?.length || 0;
  const feesCount = 0;  // Set to 0 instead of hardcoded value

  // State for mobile view
  const [isMobile, setIsMobile] = React.useState(false);

  // Using a constant for notification count instead of state since we're not updating it
  const notificationCount = 3;

  const [showMiniNotifications, setShowMiniNotifications] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('overview');

  // Reference for notification panel to enable swipe gesture
  const notificationPanelRef = React.useRef(null);
  const startXRef = React.useRef(0);

  // Check screen size on mount and when window resizes with debounce
  React.useEffect(() => {
    let timeoutId = null;

    const checkScreenSize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const isMobileView = window.innerWidth < 1024;
        setIsMobile(isMobileView);
      }, 100);
    };

    // Set initial value
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener("resize", checkScreenSize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkScreenSize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Function to toggle mini notifications panel
  const toggleMiniNotifications = () => {
    setShowMiniNotifications(!showMiniNotifications);
  };

  // Touch handlers for swipe gestures
  const handleTouchStart = (e) => {
    startXRef.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    if (!showMiniNotifications) return;

    const currentX = e.touches[0].clientX;
    const diff = startXRef.current - currentX;

    // If swiping left (to close)
    if (diff > 50) {
      e.preventDefault();
      setShowMiniNotifications(false);
    }
  };

  // Render the stats cards section
  const renderStatsCards = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
      <div className="bg-gradient-to-br from-purple-600 to-purple-800 text-white shadow-md hover:shadow-lg transition-shadow rounded-xl p-3.5 md:p-6">
        <div className="flex items-center justify-between pb-1.5 md:pb-2">
          <h3 className="text-sm md:text-lg font-bold">Students</h3>
          <Users className="h-4 w-4 md:h-6 md:w-6" />
        </div>
        <div>
          <div className="text-xl md:text-3xl font-bold">
            {isLoadingStudents ? 'Loading...' : studentCount}
          </div>
          <p className="text-xs md:text-sm flex items-center opacity-90">
            <TrendingDown className="h-3 w-3 md:h-4 md:w-4 mr-1" /> -0.03%
          </p>
        </div>
      </div>

      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white shadow-md hover:shadow-lg transition-shadow rounded-xl p-3.5 md:p-6">
        <div className="flex items-center justify-between pb-1.5 md:pb-2">
          <h3 className="text-sm md:text-lg font-bold">Staff</h3>
          <Users className="h-4 w-4 md:h-6 md:w-6" />
        </div>
        <div>
          <div className="text-xl md:text-3xl font-bold">
            {isLoadingStaff ? 'Loading...' : staffCount}
          </div>
          <p className="text-xs md:text-sm flex items-center opacity-90">
            <TrendingUp className="h-3 w-3 md:h-4 md:w-4 mr-1" /> +19.03%
          </p>
        </div>
      </div>

      <div className="bg-gradient-to-br from-rose-600 to-rose-800 text-white shadow-md hover:shadow-lg transition-shadow rounded-xl p-3.5 md:p-6 sm:col-span-2 lg:col-span-1">
        <div className="flex items-center justify-between pb-1.5 md:pb-2">
          <h3 className="text-sm md:text-lg font-bold">Outstanding Fees</h3>
          <DollarSign className="h-4 w-4 md:h-6 md:w-6" />
        </div>
        <div>
          <div className="text-xl md:text-3xl font-bold">₦{feesCount.toLocaleString()}</div>
          <p className="text-xs md:text-sm flex items-center opacity-90">
            <TrendingUp className="h-3 w-3 md:h-4 md:w-4 mr-1" /> +0.08%
          </p>
        </div>
      </div>
    </div>
  );

  // Render mobile tabs for better content organization
  const renderMobileTabs = () => (
    <div className="mb-4 border-b border-gray-200">
      <div className="flex space-x-4 overflow-x-auto scrollbar-hide pb-1">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-3 py-2 text-sm font-medium whitespace-nowrap ${
            activeTab === 'overview'
              ? 'text-purple-700 border-b-2 border-purple-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('performance')}
          className={`px-3 py-2 text-sm font-medium whitespace-nowrap ${
            activeTab === 'performance'
              ? 'text-purple-700 border-b-2 border-purple-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Performance
        </button>
        <button
          onClick={() => setActiveTab('payments')}
          className={`px-3 py-2 text-sm font-medium whitespace-nowrap ${
            activeTab === 'payments'
              ? 'text-purple-700 border-b-2 border-purple-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Payments
        </button>
        <button
          onClick={() => setActiveTab('students')}
          className={`px-3 py-2 text-sm font-medium whitespace-nowrap ${
            activeTab === 'students'
              ? 'text-purple-700 border-b-2 border-purple-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Failing Students
        </button>
      </div>
    </div>
  );

  // Render the main content section
  const renderMainContent = () => (
    <div className="flex-1 space-y-4 md:space-y-6">
      {/* Stats Cards */}
      {renderStatsCards()}

      {/* Mobile Tabs */}
      {isMobile && renderMobileTabs()}

      {/* Charts and Content based on active tab for mobile */}
      {isMobile ? (
        <div className="space-y-4">
          {activeTab === 'overview' && <AttendanceChart />}
          {activeTab === 'performance' && <PerformanceChart />}
          {activeTab === 'payments' && <RecentPayments />}
          {activeTab === 'students' && <FailingStudents />}
        </div>
      ) : (
        /* Desktop layout with all components */
        <div className="space-y-4 md:space-y-6">
          <AttendanceChart />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 md:gap-6">
            <div className="col-span-1 lg:col-span-3">
              <PerformanceChart />
            </div>
            <div className="col-span-1 lg:col-span-2">
              <FailingStudents />
            </div>
          </div>
          <RecentPayments />
        </div>
      )}
    </div>
  );

  // Mini notifications panel for mobile with swipe gesture support
  const renderMiniNotificationsPanel = () => (
    <div
      ref={notificationPanelRef}
      className={`fixed top-0 right-0 h-full w-[280px] bg-white shadow-xl z-50 transition-transform duration-300 ease-in-out ${
        showMiniNotifications ? "translate-x-0" : "translate-x-full"
      }`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
    >
      <div className="flex justify-between items-center p-3 border-b bg-gray-50">
        <div className="flex items-center space-x-2">
          <button
            onClick={toggleMiniNotifications}
            className="p-1.5 rounded-md hover:bg-gray-200 transition-colors"
            aria-label="Go back"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h2 className="text-base font-semibold">Notifications</h2>
        </div>
        <div className="flex items-center">
          {notificationCount > 0 && (
            <span className="inline-flex items-center justify-center px-2 py-0.5 mr-2 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
              {notificationCount} new
            </span>
          )}
          <button
            onClick={toggleMiniNotifications}
            className="p-1.5 rounded-md hover:bg-gray-200 transition-colors"
            aria-label="Close notifications"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="h-full overflow-y-auto pb-32 overscroll-contain">
        <NotificationsAndEvents />
      </div>
    </div>
  );

  // Render the desktop sidebar (notifications and events)
  const renderDesktopSidebar = () => <NotificationsAndEvents />;

  return (
    <div className="relative flex flex-col min-h-screen bg-background/95">
      {/* Mobile notification toggle button */}
      {isMobile && (
        <>
          <button
            onClick={toggleMiniNotifications}
            className="fixed bottom-4 right-4 z-40 p-3 rounded-full bg-primary text-white shadow-lg active:scale-95 transition-transform"
            aria-label="Toggle notifications"
          >
            <div className="relative">
              <Bell className="h-6 w-6" />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center rounded-full bg-red-500 text-white text-xs animate-pulse">
                  {notificationCount}
                </span>
              )}
            </div>
          </button>
          {renderMiniNotificationsPanel()}

          {/* Add backdrop overlay when notifications are open */}
          {showMiniNotifications && (
            <div
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
              onClick={toggleMiniNotifications}
            />
          )}
        </>
      )}

      {/* Main content */}
      <div className="flex-1 p-2 sm:p-3 md:p-6 overflow-x-hidden">
        {isMobile ? (
          <div className="w-full">{renderMainContent()}</div>
        ) : (
          <ResizablePanelGroup direction="horizontal" className="w-full gap-6 md:gap-8">
            <ResizablePanel defaultSize={75}>{renderMainContent()}</ResizablePanel>
            <ResizableHandle />
            <ResizablePanel defaultSize={25} minSize={15} maxSize={35}>
              {renderDesktopSidebar()}
            </ResizablePanel>
          </ResizablePanelGroup>
        )}
      </div>
    </div>
  );
}
